// The building blocks of the connection detail panel: the shared section
// layout and one section per entry of a connection's capabilities. A source
// section drives the sync, a tool or sink section drives the access grant,
// any other capability gets a neutral section without controls. Every string
// comes from the adminConnectors namespace and every request goes through
// apiFetchJson — credentials are never rendered.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import AgentMultiSelect from "./AgentMultiSelect";
import {
  SECONDS_PER_MINUTE,
  buildGrantBody,
  grantProblem,
  summarizeRun,
} from "../utils/connectionForm";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import "./AdminCollections.css";
import "./AdminConnectors.css";

/** One request at a time per card: the card's own buttons are disabled while
 *  it is in flight and a failure is shown in place, without clearing what the
 *  user typed. */
export function useCardRequest() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onSuccess) => {
    setBusy(true);
    setError("");
    try {
      const data = await request();
      if (onSuccess) onSuccess(data);
      return data;
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

/** A timestamp as the browser renders it; the raw value when unparsable. */
export function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

/** Badge class per health tone (see summarizeHealth). */
export const HEALTH_BADGE = {
  success: "ds-badge ds-badge--success",
  danger: "ds-badge ds-badge--danger",
  neutral: "ds-badge",
};

/** One section of the connection detail panel: a title, optional actions on
 *  the same line, then the body. */
export function DetailSection({ title, actions, children, testId }) {
  return (
    <section className="cx-section" data-testid={testId}>
      <div className="cx-section-head">
        <h4 className="cx-section-title">{title}</h4>
        {actions && <div className="cx-section-actions">{actions}</div>}
      </div>
      {children && <div className="cx-section-body">{children}</div>}
    </section>
  );
}

/** A checkbox with its label on one line; the optional explanation below the
 *  label is announced as the checkbox's description, not as part of its name. */
export function CheckRow({ id, checked, disabled, onChange, label, help }) {
  const helpId = help ? `${id}-help` : undefined;
  return (
    <div className="cx-check">
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={onChange}
             aria-describedby={helpId} />
      <div>
        <label className="cx-check-label" htmlFor={id}>{label}</label>
        {help && <div id={helpId} className="ds-help">{help}</div>}
      </div>
    </div>
  );
}

/** A form error shown in place, below the controls it is about. */
function CardError({ message }) {
  return message ? <div className="ds-alert ds-alert--danger ds-flush" role="alert">{message}</div> : null;
}

/** Whether a stored grant is enabled: only an explicit `true` counts, a
 *  missing grant is not an enabled one. */
const isGrantEnabled = (grant) => grant?.enabled === true;

/** The form state of an access card, derived from a stored grant. */
function grantFormState(grant) {
  return {
    enabled: isGrantEnabled(grant),
    roles: [...(grant.allowed_roles || [])],
    agentIds: [...(grant.allowed_agent_ids || [])],
    requiresApproval: !!grant.requires_approval,
  };
}

/** The section of a capability that reads documents (sync, schedule, grant). */
export function SourceCard({ capability, label, connection, onChanged }) {
  const { t } = useTranslation();
  const { busy, error, run } = useCardRequest();
  const grant = (connection.grants || {}).source || {};
  const storedEnabled = isGrantEnabled(grant);
  const [grantEnabled, setGrantEnabled] = useState(storedEnabled);
  const [minutes, setMinutes] = useState(
    String(Math.round((connection.schedule_seconds || 0) / SECONDS_PER_MINUTE)));

  // The parent reloads the connection after every change: pick up a new grant
  // and a new schedule without touching what the user typed meanwhile.
  useEffect(() => { setGrantEnabled(storedEnabled); }, [storedEnabled]);
  useEffect(() => {
    setMinutes(String(Math.round((connection.schedule_seconds || 0) / SECONDS_PER_MINUTE)));
  }, [connection.schedule_seconds]);

  const running = connection.status === "running";
  const summary = summarizeRun(connection.last_summary);

  // The checkbox follows the toggle at once, but a failed PUT puts it back to
  // the stored value: it must never show a state the server does not hold.
  const toggleGrant = () => {
    const next = !grantEnabled;
    setGrantEnabled(next);
    run(async () => {
      try {
        return await apiFetchJson(`/api/v1/connections/${connection.id}/grants/${capability}`, {
          method: "PUT",
          body: JSON.stringify(buildGrantBody({
            enabled: next,
            roles: grant.allowed_roles || [],
            agentIds: grant.allowed_agent_ids || [],
            requiresApproval: !!grant.requires_approval,
          })),
        });
      } catch (e) {
        setGrantEnabled(storedEnabled);
        throw e;                             // useCardRequest shows the message
      }
    }, onChanged);
  };

  const saveSchedule = () => {
    const value = Math.max(0, Math.round((Number(minutes) || 0) * SECONDS_PER_MINUTE));
    run(() => apiFetchJson(`/api/v1/connections/${connection.id}`, {
      method: "PATCH",
      body: JSON.stringify({ schedule_seconds: value }),
    }), onChanged);
  };

  return (
    <DetailSection
      title={label}
      actions={(
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy || running}
                onClick={() => run(() => apiFetchJson(`/api/v1/connections/${connection.id}/sync`, { method: "POST" }), onChanged)}>
          {t("adminConnectors.syncNow")}
        </button>
      )}
    >
      <div className="cx-status-line">
        <span className={statusBadgeClass(connection.status)}>{statusLabel(connection.status)}</span>
        <span className="ds-meta">
          {t("adminConnectors.colLastSync")}:{" "}
          {connection.last_sync_at ? formatWhen(connection.last_sync_at) : t("adminConnectors.never")}
        </span>
        {summary && <span className="ds-meta ds-mono">{summary}</span>}
      </div>
      {connection.last_error && (
        <div className="ac-cell-error">
          {t("adminConnectors.lastError")}: {connection.last_error}
        </div>
      )}
      <div className="ds-field">
        <label className="ds-label" htmlFor={`cd-schedule-${capability}`}>
          {t("adminConnectors.fieldSchedule")}
        </label>
        <div className="cx-inline">
          <input id={`cd-schedule-${capability}`} className="ds-input cx-input-short" type="number" min="0"
                 value={minutes} disabled={busy} onChange={(e) => setMinutes(e.target.value)} />
          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy} onClick={saveSchedule}>
            {t("adminConnectors.scheduleSave")}
          </button>
        </div>
      </div>
      <CheckRow id={`cd-grant-${capability}`} checked={grantEnabled} disabled={busy} onChange={toggleGrant}
                label={t("adminConnectors.grantTitle")} help={t("adminConnectors.grantHelp")} />
      <CardError message={error} />
    </DetailSection>
  );
}

/** The section of a capability that grants access to agents (tool, sink). */
export function AccessCard({ capability, label, connection, kind, roles, onChanged, showActions = false }) {
  const { t } = useTranslation();
  const { busy, error, run } = useCardRequest();
  const grant = (connection.grants || {})[capability] || {};
  const grantKey = JSON.stringify(grant);
  const [state, setState] = useState(() => grantFormState(grant));
  const [problem, setProblem] = useState("");

  // Re-read the grant whenever the parent hands over a new one, so a saved
  // grant is reflected even if the user typed something else in between.
  useEffect(() => {
    setState(grantFormState(JSON.parse(grantKey) || {}));
    setProblem("");
  }, [grantKey]);

  const toggleRole = (role, checked) => {
    setState((prev) => ({
      ...prev,
      roles: checked ? [...prev.roles, role] : prev.roles.filter((r) => r !== role),
    }));
    setProblem("");
  };

  const save = () => {
    const { agentIds } = state;
    const issue = grantProblem({ roles: state.roles, agentIds }, t);
    setProblem(issue || "");
    if (issue) return;                       // an invalid grant is never sent
    const body = buildGrantBody({
      enabled: state.enabled,
      roles: state.roles,
      agentIds,
      requiresApproval: state.requiresApproval,
    });
    run(() => apiFetchJson(`/api/v1/connections/${connection.id}/grants/${capability}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }), onChanged);
  };

  const actions = showActions ? (kind?.actions || []) : [];

  return (
    <DetailSection title={label}>
      <CheckRow id={`cd-grant-${capability}`} checked={state.enabled} disabled={busy}
                onChange={(e) => setState((prev) => ({ ...prev, enabled: e.target.checked }))}
                label={t("adminConnectors.grantTitle")} help={t("adminConnectors.grantHelp")} />
      <fieldset className="cx-chips">
        <legend className="ds-label">{t("adminConnectors.rolesTitle")}</legend>
        {(roles || []).map((role) => (
          <label key={role} className="ds-chip cx-chip-check" htmlFor={`cd-role-${capability}-${role}`}>
            <input id={`cd-role-${capability}-${role}`} type="checkbox" className="ds-visually-hidden"
                   checked={state.roles.includes(role)} disabled={busy}
                   onChange={(e) => toggleRole(role, e.target.checked)} />
            {role}
          </label>
        ))}
      </fieldset>
      <AgentMultiSelect value={state.agentIds} disabled={busy}
                        onChange={(agentIds) => setState((prev) => ({ ...prev, agentIds }))} />
      <CheckRow id={`cd-approval-${capability}`} checked={state.requiresApproval} disabled={busy}
                onChange={(e) => setState((prev) => ({ ...prev, requiresApproval: e.target.checked }))}
                label={t("adminConnectors.requireApproval")} />
      {actions.length > 0 && (
        <div className="ds-field">
          <span className="ds-label">{t("adminConnectors.actionsTitle")}</span>
          <ul className="cx-actions-list">
            {actions.map((action, index) => (
              <li key={`${action.name || "action"}-${index}`}>
                <span className="ac-cell-strong ds-mono">{action.name}</span>
                <span className="ds-meta">{action.description}</span>
                {action.requires_approval && (
                  <span className="ds-badge ds-badge--warning">{t("adminConnectors.approvalBadge")}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showActions && kind?.dynamic_actions && (
        <div className="ds-alert ds-alert--info ds-flush">{t("adminConnectors.dynamicActionsNote")}</div>
      )}
      {capability === "sink" && (
        <div className="ds-alert ds-alert--info ds-flush">{t("adminConnectors.sinkApprovalNote")}</div>
      )}
      <CardError message={problem || error} />
      <div className="cx-section-foot">
        <button type="button" className="ds-btn ds-btn--primary ds-btn--sm" disabled={busy} onClick={save}>
          {t("adminConnectors.saveAccess")}
        </button>
      </div>
    </DetailSection>
  );
}

/** A section for a capability the UI has no controls for: the name only. */
export function NeutralCard({ capability }) {
  return <DetailSection title={<span className="ds-mono">{capability}</span>} />;
}
